// Grilla reutilizable de consulta: filas seleccionables con teclado (↑ ↓ Inicio Fin), Enter abre,
// orden por columna y paginación SERVER-SIDE (la grilla nunca pide "todo"). Las columnas las declara
// cada módulo. Para edición (líneas de la nota) hay una grilla propia: esta es de lectura.
import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface Columna<T> {
  id: string;
  titulo: string;
  celda: (fila: T) => ReactNode;
  alinear?: 'izq' | 'der' | 'centro';
  ancho?: string;
  /** Si la columna se puede ordenar, la clave que se envía al servidor. */
  orden?: string;
}

/** Paginación incremental (buscadores, D-UI-2): «cargar más» agrega la página siguiente. */
export interface CargaIncremental {
  hayMas: boolean;
  cargando: boolean;
  alCargar: () => void;
}

export interface Paginacion {
  pagina: number;
  /** El servidor no dice el total: si vino una página llena, puede haber más. */
  hayMas: boolean;
  alCambiar: (pagina: number) => void;
}

export function Grilla<T>({
  columnas,
  filas,
  clave,
  alAbrir,
  alSeleccionar,
  vacio = 'Sin resultados',
  cargando = false,
  paginacion,
  orden,
  alOrdenar,
  etiqueta,
  autofoco = false,
  mas,
}: {
  columnas: Columna<T>[];
  filas: readonly T[];
  clave: (fila: T) => string;
  alAbrir?: (fila: T) => void;
  alSeleccionar?: (fila: T | null) => void;
  vacio?: string;
  cargando?: boolean;
  paginacion?: Paginacion;
  orden?: { columna: string; sentido: 'asc' | 'desc' };
  alOrdenar?: (columna: string) => void;
  etiqueta: string;
  autofoco?: boolean;
  mas?: CargaIncremental;
}) {
  const [sel, setSel] = useState(0);
  const cuerpo = useRef<HTMLTableSectionElement>(null);
  const tabla = useRef<HTMLTableElement>(null);
  // Una búsqueda NUEVA vuelve a la primera fila; «cargar más» (se agregan filas) conserva la posición.
  const primera = filas[0] ? clave(filas[0]) : '';
  useEffect(() => {
    setSel(0);
  }, [primera]);
  useEffect(() => {
    alSeleccionar?.(filas[sel] ?? null);
    cuerpo.current
      ?.querySelector<HTMLElement>(`[data-fila="${sel}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [sel, filas]);
  useEffect(() => {
    if (autofoco) tabla.current?.focus();
  }, [autofoco]);

  const mover = (d: number) => setSel((s) => Math.max(0, Math.min(filas.length - 1, s + d)));
  return (
    <div
      className="grilla-caja"
      onScroll={(e) => {
        const c = e.currentTarget;
        if (mas?.hayMas && c.scrollTop + c.clientHeight >= c.scrollHeight - 40) mas.alCargar();
      }}
    >
      <table
        ref={tabla}
        className="grilla"
        tabIndex={0}
        aria-label={etiqueta}
        aria-busy={cargando}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            if (sel >= filas.length - 1 && mas?.hayMas) mas.alCargar();
            mover(1);
          } else if (e.key === 'ArrowUp') mover(-1);
          else if (e.key === 'Home') setSel(0);
          else if (e.key === 'End') setSel(filas.length - 1);
          else if (e.key === 'PageDown' && paginacion?.hayMas)
            paginacion.alCambiar(paginacion.pagina + 1);
          else if (e.key === 'PageUp' && paginacion && paginacion.pagina > 1)
            paginacion.alCambiar(paginacion.pagina - 1);
          else if (e.key === 'Enter' && filas[sel] && alAbrir) alAbrir(filas[sel]);
          else return;
          e.preventDefault();
        }}
      >
        <thead>
          <tr>
            {columnas.map((c) => (
              <th
                key={c.id}
                style={c.ancho ? { width: c.ancho } : undefined}
                className={c.alinear ? `al-${c.alinear}` : undefined}
                aria-sort={
                  orden && c.orden && orden.columna === c.orden
                    ? orden.sentido === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
              >
                {c.orden && alOrdenar ? (
                  <button type="button" className="th-orden" onClick={() => alOrdenar(c.orden!)}>
                    {c.titulo}
                    {orden?.columna === c.orden ? (orden.sentido === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                ) : (
                  c.titulo
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody ref={cuerpo}>
          {filas.map((f, i) => (
            <tr
              key={clave(f)}
              data-fila={i}
              aria-selected={i === sel}
              className={i === sel ? 'fila-sel' : undefined}
              onClick={() => setSel(i)}
              onDoubleClick={() => alAbrir?.(f)}
            >
              {columnas.map((c) => (
                <td key={c.id} className={c.alinear ? `al-${c.alinear}` : undefined}>
                  {c.celda(f)}
                </td>
              ))}
            </tr>
          ))}
          {!filas.length && (
            <tr>
              <td colSpan={columnas.length} className="grilla-vacia">
                {cargando ? 'Cargando…' : vacio}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {mas && filas.length > 0 && (
        <div className="grilla-pie">
          <span className="apagado">
            {filas.length} {mas.hayMas ? 'mostrados · hay más' : 'en total'}
          </span>
          {mas.hayMas && (
            <button type="button" disabled={mas.cargando} onClick={mas.alCargar}>
              {mas.cargando ? 'Cargando…' : 'Cargar más (↓ al final)'}
            </button>
          )}
        </div>
      )}
      {paginacion && (
        <div className="grilla-pie">
          <button
            type="button"
            disabled={paginacion.pagina <= 1}
            onClick={() => paginacion.alCambiar(paginacion.pagina - 1)}
          >
            ◀ Anterior
          </button>
          <span>Página {paginacion.pagina}</span>
          <button
            type="button"
            disabled={!paginacion.hayMas}
            onClick={() => paginacion.alCambiar(paginacion.pagina + 1)}
          >
            Siguiente ▶
          </button>
        </div>
      )}
    </div>
  );
}
